/**
 * Report date range presets. Returns { from, to } in ISO-8601 for ReportQueryInput.
 * All times are start-of-day (00:00:00) and end-of-day (23:59:59.999) in local time,
 * then converted to ISO string for the API.
 */

export type ReportDatePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "custom";

export interface ReportDateRange {
  from: string;
  to: string;
  preset: ReportDatePreset;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
}

function toISO(d: Date): string {
  return d.toISOString();
}

/** Get Monday of the week for a given date (week starts Monday). */
function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return startOfDay(date);
}

/** Get Sunday of the week for a given date. */
function getSunday(d: Date): Date {
  const monday = getMonday(d);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return endOfDay(sunday);
}

export function getReportDateRange(
  preset: ReportDatePreset,
  customFrom?: Date,
  customTo?: Date
): ReportDateRange {
  const now = new Date();
  let from: Date;
  let to: Date;

  switch (preset) {
    case "today": {
      from = startOfDay(now);
      to = endOfDay(now);
      break;
    }
    case "yesterday": {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      from = startOfDay(yesterday);
      to = endOfDay(yesterday);
      break;
    }
    case "last7": {
      to = endOfDay(now);
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from = startOfDay(from);
      break;
    }
    case "last30": {
      to = endOfDay(now);
      from = new Date(now);
      from.setDate(from.getDate() - 29);
      from = startOfDay(from);
      break;
    }
    case "thisWeek": {
      from = startOfDay(getMonday(now));
      to = endOfDay(now);
      break;
    }
    case "lastWeek": {
      const lastMonday = getMonday(now);
      lastMonday.setDate(lastMonday.getDate() - 7);
      from = startOfDay(lastMonday);
      const lastSunday = getSunday(lastMonday);
      to = endOfDay(lastSunday);
      break;
    }
    case "thisMonth": {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = endOfDay(now);
      break;
    }
    case "custom": {
      if (customFrom && customTo) {
        from = startOfDay(new Date(customFrom));
        to = endOfDay(new Date(customTo));
      } else {
        from = startOfDay(now);
        to = endOfDay(now);
      }
      break;
    }
    default: {
      from = new Date(now);
      from.setDate(from.getDate() - 6);
      from = startOfDay(from);
      to = endOfDay(now);
      break;
    }
  }

  return {
    from: toISO(from),
    to: toISO(to),
    preset,
  };
}

export const REPORT_DATE_PRESET_LABELS: Record<ReportDatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  last30: "Last 30 days",
  thisWeek: "This week",
  lastWeek: "Last week",
  thisMonth: "This month",
  custom: "Custom range",
};
